import _extends from "@babel/runtime/helpers/extends";
import { handleActions, combineActions } from 'redux-actions';
import is from '@sindresorhus/is';
import onlyRunInDev from '../../../utils/onlyRunInDev'; // Simple helpers that normalize the action payload and options to keep
// implementations clean

var normalizePayload = function normalizePayload(payload) {
  return payload.results || [payload];
};

var normalizeOptions = function normalizeOptions(options) {
  return _extends({
    key: 'id'
  }, options);
}; // Type checking for the action map. Also ensures that at least 1 action has
// been set for `add`


var validateActionMap = onlyRunInDev(function (_ref) {
  var add = _ref.add,
      remove = _ref.remove;
  var message = 'createByIdReducer was called without a valid actionMap key' + 'Please ensure you have an `add` and `remove` key set under `actionMap`' + 'and that `add` and `remove` are arrays. `add` requires at least 1 action';

  if (!add || !remove) {
    throw new Error(message);
  }

  if (!is.array(add) || !is.array(remove)) {
    throw new Error(message);
  }

  if (add.length < 1) {
    throw new Error(message);
  }
}); // Ensures that each item that is being added / removed has a valid id. Returns
// true if it does, false if it doesn't. We want to keep these checks on in
// production and testing since they are more likely to slip through local
// testing

var isValidItem = function isValidItem(item, key) {
  if (!is.object(item)) {
    console.error("a byId reducer attempted to add the following item, but failed because " + ("it wasn't an object:`" + item + "`. Skipping... "));
    return false;
  }

  if (!item[key]) {
    console.error("a byId reducer attempted to add the following item, but failed " + ("because it didn't have a value set for the `" + key + "` key: ") + ("`" + JSON.stringify(item) + "`. Skipping..."));
    return false;
  }

  return true;
}; // The two types of updates


var add = function add(state, item, key) {
  var _extends2;

  return _extends({}, state, (_extends2 = {}, _extends2[item[key]] = item, _extends2));
};

var remove = function remove(state, item, key) {
  var newState = _extends({}, state);

  delete newState[item[key]];
  return newState;
}; // Helper that handles iterating over each item in the action payload and
// calling the relevant update function for each.


var updateState = function updateState(_ref2, updater) {
  var key = _ref2.key;
  return function (state, action) {
    var data = normalizePayload(action.payload);
    return data.reduce(function (newState, item) {
      if (!isValidItem(item, key)) {
        return newState;
      }

      return updater(newState, item, key);
    }, state);
  };
};
/**
 * Creates a new byId reducer. The byId reducer supports two types of updates:
 *
 * - `add` adds 1 or more items to the reducer.
 * - `remove` removes 1 or more items from the reducer
 *
 * Both `add` and `remove` expect the action payload to be either an object representing a single item or an array of items.
 * Note that the byId reducer is configured to only work with action payloads that contain DRF response data.
 *
 * Ie, the payload must be either:
 *
 * 1. A single item
 * 2. An object with multiple items under the `results` key
 *
 * If your response does not match that paradigm, you should format your data to match this structure via the [payloadCreator](https://redux-actions.js.org/api/createaction#createactiontype-payloadcreator) function for `createAction`
 *
 * ```js
 * // Example Implementation
 *
 * import { createByIdReducer } from '@minerva-analytics/pantheon';
 *
 * const someAction = (data) => ({
 *   type: 'SOME_ACTION',
 *   payload: data,
 * });
 *
 * const byId = createByIdReducer({
 *   actionMap: {
 *     // You need to provide at least 1 action to `add`
 *     add: [someAction],
 *     // You can omit actions for remove but still need to provide an empty array
 *     remove: []
 *   },
 *   // All options are optional, the defaults are highlighted below
 *   options: {
 *     key: 'id',
 *   }
 * })
 * ```
 *
 * @param {Object} config - The configuration for the byId reducer
 * @param {Object} config.actionMap - The mapping of actions to updates
 * @param {Array} config.actionMap.add - The array of actions that should trigger an `add` update
 * @param {Array} config.actionMap.remove - The array of actions that should trigger a `remove` update
 * @param {Object} config.options - Possible options to configure the byId reducer
 * @param {String} config.options.key - The key to use as the id for each object. Defaults to `id`
 *
 * @returns {Function} - A newly initialized `byId` reducer
 */


var createByIdReducer = function createByIdReducer(_ref3) {
  var _ref3$actionMap = _ref3.actionMap,
      actionMap = _ref3$actionMap === void 0 ? {} : _ref3$actionMap,
      _ref3$options = _ref3.options,
      options = _ref3$options === void 0 ? {} : _ref3$options;
  validateActionMap(actionMap);
  var opts = normalizeOptions(options);
  var curriedAdd = updateState(opts, add);
  var curriedRemove = updateState(opts, remove);
  var reducerMap = new Map([[combineActions.apply(void 0, actionMap.add), curriedAdd]]); // Only support remove if the user has included an action for it

  if (actionMap.remove.length > 0) {
    reducerMap.set(combineActions.apply(void 0, actionMap.remove), curriedRemove);
  }

  return handleActions(reducerMap, {});
};

export default createByIdReducer;